import { type ConstructorClassWithArgs } from "../../../types/internal/utility";
import type BaseAgent from "./BaseAgent";

class AgentManagerSingleton {
    protected agents: Array<BaseAgent>;

    constructor() {
        this.agents = [];
    }

    public addAgent(agent: BaseAgent): void {
        this.agents.push(agent);
    }

    /**
     * Get every agent currently registered, regardless of type.
     */
    public getAgents(): ReadonlyArray<BaseAgent> {
        return this.agents;
    }

    public closeAgents(): void {
        for(let i = 0; i < this.agents.length; i++) {
            const agent = this.agents[i];

            // Check to see if the close function hasn't been overridden
            if(typeof agent.close === "function") {
                agent.close();
            }
        }

        if(process.env.NODE_ENV === "test") {
            this.agents = [];
        }
    }

    public getAgent<T extends BaseAgent>(type: ConstructorClassWithArgs<T>): T;
    public getAgent<T extends BaseAgent, TC extends ConstructorClassWithArgs<T>>(
        type: TC,
        args: ConstructorParameters<TC>
    ): T;
    public getAgent<T extends BaseAgent, TC extends ConstructorClassWithArgs<T>>(
        type: TC,
        args?: ConstructorParameters<TC>
    ): T {
        for(let i = 0, l = this.agents.length; i < l; i++) {
            if(this.agents[i] instanceof type) {
                return this.agents[i] as T;
            }
        }

        if(typeof args !== "undefined") {
            return new type(...args);
        }

        return new type();
    }
}

// Create singleton
const AgentManager = new AgentManagerSingleton();

// Check if window instance exists
if(typeof window !== "undefined") {
    // Make agents send closing event on closure
    window.addEventListener("unload", () => {
        AgentManager.closeAgents();
    });
}

// Export singleton
export default AgentManager;
